/**
 * resolvePassSummary — the one-line chat summary posted when a bulk
 * "Resolve issues" pass finishes.
 *
 * The pass itself (server resolve_worker) and its review queue are unchanged;
 * this only reads back what the pass produced so the writer isn't left
 * guessing. A flag counts as resolved when its proposal changes the text AND
 * the flag's marker is gone from the proposed text; everything else stayed in
 * place (the library didn't have what the flag asks for).
 *
 * Pure — no I/O; the shell fetches the proposals and posts the line.
 */

import type { SectionProposal } from "../../api/client";

/** One target section of the pass, as the Resolve-issues wizard started it. */
export interface ResolvePassTarget {
  module_id: string;
  number: string;
  issue_ids: string[];
}

/** Issue ids this proposal actually resolved (marker gone from the new text). */
function resolvedIssueIds(p: SectionProposal): string[] {
  if ((p.proposed_text ?? "") === (p.original_text ?? "")) return [];
  const proposed = p.proposed_text ?? "";
  // issue_ids and marker_texts are parallel when both are present; without
  // markers, a changed proposal counts all of its issues as resolved.
  if (p.marker_texts?.length === p.issue_ids.length) {
    return p.issue_ids.filter((_, i) => !proposed.includes(p.marker_texts[i]));
  }
  return p.issue_ids;
}

/**
 * Server timestamp → epoch ms. The API serialises naive UTC datetimes with no
 * zone ("2026-09-22T09:00:30.138000"), which ``Date.parse`` would read as the
 * browser's LOCAL time — in IST that put every proposal 5½ h before the pass
 * and the summary counted none of them. A string without a zone is UTC.
 */
export function parseServerTime(value: string): number {
  const hasZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value.trim());
  return Date.parse(hasZone ? value : `${value}Z`);
}

/**
 * The pass's own proposals: created at/after the pass started (a minute of
 * slack for client/server clock skew). Rows without a timestamp are kept —
 * the issue-id match in ``summariseResolvePass`` still scopes them.
 */
export function proposalsSincePassStart(proposals: SectionProposal[], startedAtMs: number): SectionProposal[] {
  const since = startedAtMs - 60_000;
  return proposals.filter((p) => {
    if (!p.created_at) return true;
    const t = parseServerTime(p.created_at);
    return Number.isNaN(t) || t >= since;
  });
}

function flagWord(n: number): string {
  return n === 1 ? "flag" : "flags";
}

/**
 * Build the summary line. ``proposals`` may include unrelated rows — only
 * proposals for the pass's own issues are counted.
 */
export function summariseResolvePass(targets: ResolvePassTarget[], proposals: SectionProposal[]): string {
  const targetIds = new Set(targets.flatMap((t) => t.issue_ids));
  const total = targetIds.size;
  const resolved = new Set<string>();
  for (const p of proposals) {
    for (const id of resolvedIssueIds(p)) if (targetIds.has(id)) resolved.add(id);
  }
  const done = resolved.size;
  const left = total - done;
  const where = targets.length === 1 ? `§${targets[0].number}` : `${targets.length} sections`;
  const review = "Review the proposals in the Issues tab.";

  if (total === 0) return `Resolve issues finished for ${where} — there were no open flags to resolve.`;
  if (done === total) return `${where}: resolved all ${total} ${flagWord(total)}. ${review}`;
  if (done === 0) {
    return `${where}: couldn't resolve ${total === 1 ? "the flag" : `any of the ${total} flags`} — the information wasn't found in your sources.`;
  }
  return `${where}: resolved ${done} of ${total} ${flagWord(total)} — ${left} couldn't be resolved from your sources. ${review}`;
}
